import React from 'react';
import { Scale, Shield, FileText, AlertCircle } from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const sections = [
    {
      icon: FileText,
      title: 'Acceptance of Terms',
      content: `By accessing and using the Angel Cite website, mobile application, or services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.`,
    },
    {
      icon: AlertCircle,
      title: 'Eligibility',
      content: `You must be at least 18 years old and have the legal capacity to enter into a binding contract to use our services. By using Angel Cite, you represent that you meet these requirements.`,
    },
    {
      icon: Shield,
      title: 'Account Registration',
      content: `When you create an account with us, you must provide accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.`,
    },
    {
      icon: Scale,
      title: 'Product Information',
      content: `We strive to provide accurate product descriptions, pricing, and availability information. However, we do not warrant that such information is complete, reliable, current, or error-free. Prices are subject to change without notice.`,
    },
    {
      icon: AlertCircle,
      title: 'Order Acceptance',
      content: `Your receipt of an order confirmation does not constitute our acceptance of your order. We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in pricing, or suspected fraudulent activity.`,
    },
    {
      icon: Shield,
      title: 'Delivery',
      content: `Delivery times are estimates only. We are not responsible for delays due to circumstances beyond our reasonable control. Risk of loss passes to you upon delivery of products to the carrier.`,
    },
    {
      icon: Scale,
      title: 'Returns & Refunds',
      content: `Returns must be initiated within 7 days of delivery. Products must be unopened, unused, and in original packaging. Refunds are processed within 5-7 business days after we receive and inspect returned items.`,
    },
    {
      icon: AlertCircle,
      title: 'Intellectual Property',
      content: `All content on the Angel Cite platform, including text, graphics, logos, and software, is our property or the property of our licensors and is protected by copyright and other intellectual property laws.`,
    },
    {
      icon: Shield,
      title: 'Limitation of Liability',
      content: `To the fullest extent permitted by law, Angel Cite shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.`,
    },
    {
      icon: Scale,
      title: 'Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of Zimbabwe, without regard to its conflict of law provisions.`,
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Scale className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-300 dark:text-gray-600 mb-8 text-center transition-colors duration-300">
            Please read these terms carefully before using our services.
          </p>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={index}
                className="border-l-4 border-accent dark:border-primary pl-6 py-4 transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                    <section.icon className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
                    {section.title}
                  </h3>
                </div>
                <p className="text-gray-300 dark:text-gray-600 leading-relaxed transition-colors duration-300">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div className="mt-12 p-6 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
            <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-3 transition-colors duration-300">
              Contact Us
            </h3>
            <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2">
              <a
                href="mailto:legal@angelcite.com"
                className="text-accent dark:text-primary hover:underline transition-colors duration-300"
              >
                legal@angelcite.com
              </a>
            </p>
          </div>

          {/* Important Notice */}
          <div className="mt-8 p-4 bg-accent/10 dark:bg-primary/10 border border-accent/30 dark:border-primary/30 rounded-lg transition-colors duration-300">
            <p className="text-sm text-white dark:text-dark-dark text-center transition-colors duration-300">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;